import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../../../API';
import { normalizeStainBlock } from '../../../../../utils/metadataNormalization';

/**
 * Hook for managing file metadata editing (region/stain selection)
 */
export function useFileMetadata({ files, setFiles, doc, setDoc, networkId }) {
    const queryClient = useQueryClient();
    const [createModal, setCreateModal] = useState({open: false, type: null, context: null});

    const updateItemMetadataMutation = useMutation({
        mutationFn: ({itemId, stain, regions, blockId, level, scan}) => {
            const payload = {};
            if (stain !== undefined) payload.stain = stain;
            if (regions !== undefined) payload.regions = regions;
            if (blockId !== undefined) payload.blockId = blockId;
            if (level !== undefined) payload.level = level;
            if (scan !== undefined) payload.scan = scan;
            return api.put(`/bdsa/item/${itemId}`, { body: { body: payload } });
        },
        onSuccess: (d) => {
            if (d.status === 200 && d.data) {
                const updatedBdsaItem = d.data;
                const itemId = updatedBdsaItem.itemId;
                if (doc && doc.items) {
                    const updatedItems = doc.items.map(item => {
                        if (String(item._id) === String(itemId)) {
                            const updatedItem = { ...item };
                            if (!updatedItem.bdsaData) {
                                updatedItem.bdsaData = {};
                            }
                            updatedItem.bdsaData.stain = updatedBdsaItem.stain;
                            updatedItem.bdsaData.regions = updatedBdsaItem.regions;
                            updatedItem.bdsaData.blockId = updatedBdsaItem.blockId;
                            if (updatedBdsaItem.level !== undefined) updatedItem.bdsaData.level = updatedBdsaItem.level;
                            if (updatedBdsaItem.scan !== undefined) updatedItem.bdsaData.scan = updatedBdsaItem.scan;
                            if (updatedBdsaItem.itemName) {
                                updatedItem.name = updatedBdsaItem.itemName;
                            }
                            return updatedItem;
                        }
                        return item;
                    });
                    setDoc({ ...doc, items: updatedItems });
                }
            }
        },
        onError: (error) => {
            // Error handling - the error message will be displayed by React Query
            console.error('Failed to update item metadata:', error);
        },
    });

    const deleteItemMutation = useMutation({
        mutationFn: (itemId) => api.delete(`/item/${itemId}`),
        onSuccess: (d, itemId) => {
            if (d.status === 200) {
                const items = doc?.items || [];
                const updatedItems = items.filter(i => i._id !== itemId);
                setDoc({...doc, items: updatedItems});
                queryClient.invalidateQueries({ queryKey: ['bdsa-case', networkId] });
            }
        },
    });

    // Handle modal success - auto-select newly created item
    const handleModalSuccess = useCallback((newItem) => {
        // Refresh queries to get updated lists
        queryClient.invalidateQueries({ queryKey: ['bdsa-stain'] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-block'] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-region'] });
        queryClient.invalidateQueries({ queryKey: ['bdsa-metadata-options'] });
        
        // Auto-select in the context that triggered creation
        if (createModal.context) {
            const { file, item, field } = createModal.context;
            if (file) {
                setFiles(prev => {
                    const updated = {...prev};
                    if (updated[file.name]) {
                        updated[file.name][field] = newItem;
                        // When creating a block from file row, set regions from block's regionDefinitions so upload/move get them
                        if (field === 'block' && newItem?.regionDefinitions) {
                            updated[file.name].regions = Array.isArray(newItem.regionDefinitions) ? newItem.regionDefinitions : [];
                        }
                    }
                    return updated;
                });
            } else if (item) {
                const updateData = { itemId: item._id };
                if (field === 'stain') {
                    updateData.stain = newItem;
                } else if (field === 'region' || field === 'regions') {
                    updateData.regions = Array.isArray(newItem) ? newItem : [newItem];
                } else if (field === 'block') {
                    updateData.regions = Array.isArray(newItem?.regions) ? newItem.regions : (newItem ? [newItem] : undefined);
                    updateData.blockId = newItem?._id ?? null;
                }
                updateItemMetadataMutation.mutate(updateData);
            }
        }
        
        setCreateModal({ open: false, type: null, context: null });
    }, [createModal, queryClient, setFiles, updateItemMetadataMutation]);

    // Update file metadata
    const updateFileMetadata = useCallback((fileName, field, value) => {
        setFiles(prevFiles => {
            const updatedFiles = {...prevFiles};
            if(updatedFiles[fileName]) {
                updatedFiles[fileName][field] = value;
            }
            return updatedFiles;
        });
    }, [setFiles]);

    // Update item metadata (stain, regions, blockId, level, scan)
    const updateItemMetadata = useCallback((itemId, metadata) => {
        const stain = metadata?.stain !== undefined ? metadata.stain : undefined;
        const regions = metadata?.regions !== undefined ? metadata.regions : undefined;
        const blockId = metadata?.blockId !== undefined ? metadata.blockId : undefined;
        const level = metadata?.level !== undefined ? metadata.level : undefined;
        const scan = metadata?.scan !== undefined ? metadata.scan : undefined;
        updateItemMetadataMutation.mutate({
            itemId,
            stain,
            regions,
            blockId,
            level,
            scan
        });
    }, [updateItemMetadataMutation]);

    return {
        createModal,
        setCreateModal,
        handleModalSuccess,
        updateFileMetadata,
        updateItemMetadata,
        deleteItemMutation,
        updateItemMetadataMutation
    };
}
